import { ApolloClient } from "@apollo/client";

export type RefusedQuery = {
  reducerPath: string;
  endpointName: string;
  originalArgs: unknown;
};

type QueryEntry = {
  status?: string;
  endpointName?: string;
  originalArgs?: unknown;
  error?: { status?: unknown };
};

/**
 * The cached RTK Query calls the server refused with 403 (the two-step code
 * screen came up on top of the app, QA batch 18 / 2FA-036). Only those are
 * asked again after the code is entered; other errors are left alone.
 */
export const refusedQueries = (
  state: Record<string, unknown>,
  reducerPaths: string[],
): RefusedQuery[] => {
  const found: RefusedQuery[] = [];
  reducerPaths.forEach((reducerPath) => {
    const queries = (
      state[reducerPath] as { queries?: Record<string, QueryEntry> } | undefined
    )?.queries;
    if (!queries) return;
    Object.values(queries).forEach((entry) => {
      if (
        entry?.status === "rejected" &&
        entry.endpointName &&
        entry.error?.status === 403
      ) {
        found.push({
          reducerPath,
          endpointName: entry.endpointName,
          originalArgs: entry.originalArgs,
        });
      }
    });
  });
  return found;
};

type ApiLike = {
  reducerPath: string;
  endpoints: Record<string, unknown>;
};

type Initiate = (
  arg: unknown,
  options: { forceRefetch: boolean; subscribe: boolean },
) => unknown;

/**
 * After the two-step code was entered on top of the app: asks the server
 * again for everything it refused meanwhile, so the page behind fills in
 * without a reload. RTK Query: every refused (403) query, by its own
 * arguments (the page's hooks see the new answer). GraphQL: Apollo's active
 * queries. Returns how many RTK queries were asked again.
 */
export const refetchRefused = (
  store: { getState: () => unknown; dispatch: (action: never) => unknown },
  apis: ApiLike[],
  apollo?: Pick<ApolloClient<unknown>, "refetchQueries"> | null,
): number => {
  const byPath = new Map(apis.map((api) => [api.reducerPath, api]));
  const refused = refusedQueries(
    store.getState() as Record<string, unknown>,
    Array.from(byPath.keys()),
  );
  refused.forEach((query) => {
    const endpoint = byPath.get(query.reducerPath)?.endpoints[
      query.endpointName
    ] as { initiate?: Initiate } | undefined;
    if (!endpoint?.initiate) return;
    store.dispatch(
      endpoint.initiate(query.originalArgs, {
        forceRefetch: true,
        subscribe: false,
      }) as never,
    );
  });
  try {
    void apollo?.refetchQueries({ include: "active" })?.catch?.(() => {
      // a failed refetch shows in the page's own error state
    });
  } catch {
    // ignore: Apollo not ready
  }
  return refused.length;
};
